import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  buildInitialState,
  buildSpecTree,
  executeSchemaFunction,
  resolveActionDefinition,
  resolveDynamicValues
} from '../ui-spec-renderer/specrenderer';
import {
  defaultComponentRegistry,
  getMissingRegistryComponents,
  renderLayoutTree
} from '../ui-spec-renderer/layoutrenderer';
import specSchema from '../ui-spec-renderer/json-schemas/spec-schema.json';
import uiComponentsSchema from '../ui-spec-renderer/json-schemas/ui-components-schema.json';
import promptInputService from '../features/promptInput/api';

const UI_SPEC_RENDERER_BASE_URL =
  process.env.REACT_APP_UI_SPEC_RENDERER_URL || '';

// This page is a simple demo route.
// Instead of hardcoding UI, it builds the screen from JSON files at runtime.

function normalizeAndDedupeUsers(users) {
  if (!Array.isArray(users)) return [];

  const seenIds = new Set();
  const seenEmails = new Set();

  return users
    .map((item) => ({
      ...item,
      id: Number(item?.id),
      name: String(item?.name ?? '').trim(),
      role: String(item?.role ?? '').trim(),
      email: String(item?.email ?? '').trim().toLowerCase(),
      createdAt: String(item?.createdAt ?? ''),
      status: item?.status === 'inactive' ? 'inactive' : 'active'
    }))
    .filter((item) => Number.isFinite(item.id) && item.id > 0)
    .filter((item) => item.name && item.role && item.email)
    .filter((item) => {
      if (seenIds.has(item.id) || seenEmails.has(item.email)) return false;
      seenIds.add(item.id);
      seenEmails.add(item.email);
      return true;
    });
}

// Walk the spec's return tree (using the same key-generation order as toReactNode)
// and apply user text edits before sending to the backend on Accept.
function applyTextOverridesToSpec(spec, overrides) {
  if (!spec || !overrides || Object.keys(overrides).length === 0) return spec;
  const clone = JSON.parse(JSON.stringify(spec));

  function walk(node, path) {
    if (!node || typeof node !== 'object') return;
    const { children } = node;
    if (typeof children === 'string') {
      const key = `${path}-t0`;
      if (overrides[key] !== undefined) node.children = overrides[key];
    } else if (Array.isArray(children)) {
      node.children = children.map((child, i) => {
        if (typeof child === 'string') {
          const key = `${path}-t${i}`;
          return overrides[key] !== undefined ? overrides[key] : child;
        }
        return child;
      });
      node.children.forEach((child, i) => {
        if (child && typeof child === 'object') walk(child, `${path}-${i}`);
      });
    }
  }

  walk(clone.return, 'root');
  return clone;
}

const RendererTest = () => {
  const location = useLocation();
  const navigate = useNavigate();
  // Basic page states so users see progress or a friendly message if something fails.
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [tree, setTree] = useState(null);
  const [activeSpec, setActiveSpec] = useState(null);
  const [runtimeState, setRuntimeState] = useState(null);
  const [acceptStatus, setAcceptStatus] = useState(null); // null | { ok: true, msg } | { ok: false, msg }
  const [editMode, setEditMode] = useState(false);
  const [textOverrides, setTextOverrides] = useState({});

  const onTextChange = useCallback((key, newText) => {
    setTextOverrides(prev => ({ ...prev, [key]: newText }));
  }, []);

  const handleAccept = useCallback(async () => {
    setAcceptStatus(null);
    try {
      const specToSave = applyTextOverridesToSpec(activeSpec, textOverrides);
      const res = await fetch(`${UI_SPEC_RENDERER_BASE_URL}/api/accept-spec`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ spec: specToSave }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || 'Failed to save feature.');
      setAcceptStatus({ ok: true, msg: 'Feature saved! Navigating…', route: data.route, files: data.createdFiles });
      setTimeout(() => navigate(data.route), 1500);
    } catch (err) {
      setAcceptStatus({ ok: false, msg: err.message });
    }
  }, [navigate, activeSpec, textOverrides]);

  // Keep a ref so async API callbacks always read the latest state
  // without needing runtimeState in useCallback dependency arrays.
  const runtimeStateRef = useRef(null);
  useEffect(() => { runtimeStateRef.current = runtimeState; }, [runtimeState]);

  const executeSchemaAction = useCallback(async (definition, eventContext = {}) => {
    if (!activeSpec) return;

    const currentState = runtimeStateRef.current || buildInitialState(activeSpec?.state || {});
    const resolvedAction = resolveActionDefinition(definition, currentState, eventContext);
    if (!resolvedAction?.functionName) return;

    const fnSpec = activeSpec?.functions?.[resolvedAction.functionName];
    if (!fnSpec) return;

    if (fnSpec.type === 'api') {
      const { transport, output, updatesState: fnUpdatesState } = fnSpec;
      if (!transport?.url) return;

      const { method = 'POST', url, responseType = 'json', body = {}, headers = {} } = transport;
      let requestBody;
      const requestHeaders = { ...headers };

      const userId = sessionStorage.getItem('user_id') || localStorage.getItem('userId') || '';
      const sessionArgs = { advisor_id: userId, user_id: userId };
      const mergedArgs = { ...sessionArgs, ...resolvedAction.args };

      // Resolve ${args.X} tokens in the URL (used by GET endpoints with query params)
      const resolvedUrl = url.replace(/\$\{args\.([^}]+)\}/g, (_, key) => {
        const val = mergedArgs[key];
        return val !== undefined ? encodeURIComponent(String(val)) : '';
      });

      if (body.formdata) {
        const resolved = resolveDynamicValues(body.formdata, currentState, {
          args: mergedArgs,
          event: eventContext
        });
        const fd = new FormData();
        Object.entries(resolved).forEach(([k, v]) => {
          if (v == null || String(v).startsWith('${')) return;
          if (Array.isArray(v)) {
            v.forEach(item => { if (item instanceof File) fd.append(k, item); });
          } else if (v instanceof File) {
            fd.append(k, v);
          } else {
            fd.append(k, String(v));
          }
        });
        if (userId && !fd.has('advisor_id')) fd.append('advisor_id', userId);
        requestBody = fd;
      } else if (body.json) {
        requestHeaders['Content-Type'] = 'application/json';
        const resolved = resolveDynamicValues(body.json, currentState, {
          args: mergedArgs,
          event: eventContext
        });
        const sanitized = Object.fromEntries(
          Object.entries(resolved).filter(([, v]) => !(v instanceof File) && !Array.isArray(v))
        );
        requestBody = JSON.stringify(sanitized);
      } else if (body.raw) {
        requestHeaders['Content-Type'] = transport.contentType || 'application/json';
        const resolved = resolveDynamicValues(body.raw, currentState, {
          args: mergedArgs,
          event: eventContext
        });
        requestBody = typeof resolved === 'string' ? resolved : JSON.stringify(resolved);
      }

      try {
        const response = await fetch(resolvedUrl, { method, headers: requestHeaders, body: method === 'GET' ? undefined : requestBody });
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

        const data = responseType === 'text' ? await response.text() : await response.json();

        // Map response fields using output.map (e.g. "$.response", "$.data", "$")
        const outputMap = output?.map || {};
        const out = {};
        Object.entries(outputMap).forEach(([key, path]) => {
          if (!path || path === '$') {
            out[key] = data;
          } else if (typeof path === 'string' && path.startsWith('$.')) {
            out[key] = path.slice(2).split('.').reduce((acc, k) => (acc == null ? undefined : acc[k]), data);
          }
        });
        // SQL agent returns result rows as a JSON string — parse any string that looks like an array
        Object.keys(out).forEach((key) => {
          const val = out[key];
          if (typeof val === 'string' && val.trimStart().startsWith('[')) {
            try { out[key] = JSON.parse(val); } catch { /* leave as-is */ }
          }
        });

        // Resolve updatesState expressions with $out.* support
        const resolvedUpdates = resolveDynamicValues(fnUpdatesState || {}, currentState, {
          args: mergedArgs,
          event: eventContext,
          out
        });

        setRuntimeState(prev => {
          const prevState = prev || currentState;
          // Keep illustrative data when the agent returns empty — only replace a
          // non-empty array if the incoming value is also a non-empty array.
          const safeUpdates = Object.fromEntries(
            Object.entries(resolvedUpdates).map(([key, value]) => {
              const existing = prevState[key];
              if (Array.isArray(existing) && existing.length > 0 &&
                  (!Array.isArray(value) || value.length === 0)) {
                return [key, existing];
              }
              return [key, value];
            })
          );
          return { ...prevState, ...safeUpdates };
        });
      } catch (err) {
        console.error(`[RendererTest] API call failed (${url}):`, err);
      }
    } else {
      // Local function: synchronous state update
      setRuntimeState(prev => {
        const state = prev || currentState;
        return executeSchemaFunction(
          activeSpec,
          resolvedAction.functionName,
          state,
          resolvedAction.args,
          eventContext
        );
      });
    }
  }, [activeSpec]);

  // This function is passed down to all components and used to bind actions defined in the JSON schema.
  const bindAction = useCallback(({ eventName, definition }) => {
    if (!definition) return undefined;

    return (...eventArgs) => {
      const primaryEvent = eventArgs[0];
      const secondaryValue = eventArgs[1];
      const target = primaryEvent?.target;

      const resolvedValue =
        secondaryValue !== undefined
          ? secondaryValue
          : target?.value !== undefined
            ? target.value
            : primaryEvent?.value !== undefined
              ? primaryEvent.value
              : primaryEvent;

      const eventContext = {
        eventName,
        value: resolvedValue,
        rawEvent: primaryEvent,
        target,
        checked:
          target?.checked !== undefined
            ? Boolean(target.checked)
            : typeof primaryEvent === 'boolean'
              ? primaryEvent
              : undefined,
        files: Array.isArray(resolvedValue)
          ? resolvedValue
          : target?.files
            ? Array.from(target.files)
            : undefined
      };

      executeSchemaAction(definition, eventContext);
    };
  }, [executeSchemaAction]);

  useEffect(() => {
    // Prevents setting React state after the component has been unmounted.
    let mounted = true;

    const loadTree = async () => {
      // Start with a clean loading state.
      setIsLoading(true);
      setError('');
      setTree(null);
      setRuntimeState(null);

      try {
        const missingComponents = getMissingRegistryComponents(
          defaultComponentRegistry,
          uiComponentsSchema
        );

        if (missingComponents.length > 0) {
          throw new Error(
            `Renderer registry is missing schema components: ${missingComponents.join(', ')}`
          );
        }

        const fetchedSpec = await promptInputService.getActiveSpec();
        const specToRender = fetchedSpec?.spec ? fetchedSpec.spec : fetchedSpec;

        if (!specToRender || typeof specToRender !== 'object') {
          throw new Error('Invalid active spec received from API.');
        }

        if (mounted) setActiveSpec(specToRender);

        const initialState = buildInitialState(specToRender?.state || {});
        const mergedState = {
          ...initialState,
          users: normalizeAndDedupeUsers(initialState.users)
        };

        const hydratedState = executeSchemaFunction(
          specToRender,
          '__hydrate__',
          mergedState
        );

        if (mounted) setRuntimeState(hydratedState);
      } catch (buildError) {
        // Show a clear message if schema parsing/validation fails.
        if (mounted) {
          setError(buildError?.message || 'Failed to build renderer tree.');
        }
      } finally {
        // Stop loading indicator in all cases.
        if (mounted) setIsLoading(false);
      }
    };

    loadTree();

    return () => {
      mounted = false;
    };
  }, [location.state?.refreshAt]);

  useEffect(() => {
    if (runtimeState == null || !activeSpec) return;

    try {
      const nextTree = buildSpecTree(activeSpec, {
        uiComponents: uiComponentsSchema,
        specSchema,
        state: runtimeState,
        bindAction,
        availableComponents: defaultComponentRegistry
      });

      setTree(nextTree);
      setError('');
    } catch (buildError) {
      setError(buildError?.message || 'Failed to build renderer tree.');
      setTree(null);
    }
  }, [activeSpec, bindAction, runtimeState]);

  // Minimal loading UI.
  if (isLoading) {
    return <div className="p-8">Loading renderer test...</div>;
  }

  // Minimal error UI.
  if (error) {
    return (
      <div className="p-8">
        <p className="text-red-600 font-medium">Unable to render page schema.</p>
        <p className="text-gray-600 mt-1">{error}</p>
      </div>
    );
  }

  // Final step: convert the normalized tree into real React elements and render it.
  return (
    <div>
      <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1000, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
        {acceptStatus && (
          <div style={{
            padding: '12px 16px',
            borderRadius: 8,
            fontSize: 13,
            fontFamily: "'Mulish', sans-serif",
            background: acceptStatus.ok ? '#f0fdf4' : '#fef2f2',
            color: acceptStatus.ok ? '#166534' : '#991b1b',
            border: `1px solid ${acceptStatus.ok ? '#bbf7d0' : '#fecaca'}`,
            maxWidth: 380,
          }}>
            <div style={{ fontWeight: 600, marginBottom: acceptStatus.ok ? 8 : 0 }}>
              {acceptStatus.msg}
            </div>
            {acceptStatus.ok && acceptStatus.files && (
              <div style={{ fontSize: 11, color: '#4b7c61', marginBottom: 10, lineHeight: 1.6 }}>
                {acceptStatus.files.map(f => (
                  <div key={f} style={{ fontFamily: 'monospace' }}>{'• ' + f.split('/').slice(-3).join('/')}</div>
                ))}
              </div>
            )}
            {acceptStatus.ok && acceptStatus.route && (
              <button
                onClick={() => navigate(acceptStatus.route)}
                style={{
                  display: 'inline-block',
                  marginTop: 4,
                  padding: '6px 14px',
                  borderRadius: 6,
                  background: '#16a34a',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: 12,
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                Go to {acceptStatus.route} →
              </button>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={() => {
              if (!tree) return;
              if (editMode) {
                // Commit edits into activeSpec so buildSpecTree picks them up
                const updated = applyTextOverridesToSpec(activeSpec, textOverrides);
                setActiveSpec(updated);
                setTextOverrides({});
              }
              setEditMode(m => !m);
            }}
            disabled={!tree}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              background: !tree ? '#e5e7eb' : editMode ? 'var(--color-secondary-dark, #1d4ed8)' : 'var(--color-secondary, #2563eb)',
              color: !tree ? '#9ca3af' : '#fff',
              fontWeight: 600,
              fontSize: 14,
              fontFamily: "'Mulish', sans-serif",
              border: 'none',
              cursor: tree ? 'pointer' : 'not-allowed',
              boxShadow: tree ? '0 2px 8px rgba(0,0,0,0.15)' : 'none',
              transition: 'background 0.2s',
            }}
          >
            {editMode ? 'Done Editing' : 'Edit'}
          </button>
          <button
            onClick={handleAccept}
            style={{
              padding: '10px 24px',
              borderRadius: 8,
              background: '#2563eb',
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              fontFamily: "'Mulish', sans-serif",
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            Accept
          </button>
        </div>
      </div>
      {renderLayoutTree(tree, defaultComponentRegistry, { editMode, overrides: textOverrides, onTextChange, specState: runtimeState })}
    </div>
  );
};

export default RendererTest;